import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./components/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { DemoDataProvider } from './contexts/DemoDataContext';
import React, { lazy, Suspense } from 'react'; 
import { SubscriptionLimitsProvider } from './contexts/SubscriptionLimitsContext';
import { DashboardCustomizationProvider } from './contexts/DashboardCustomizationContext';
import GlobalCommandPalette from "./components/layout/GlobalCommandPalette";
import ShortcutsHelpDialog from "./components/ui/shortcuts-help-dialog";

// Lazy load all pages for better code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clients = lazy(() => import("./pages/Clients"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectTasks = lazy(() => import("./pages/ProjectTasks"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Settings = lazy(() => import("./pages/Settings"));
const TimeManagement = lazy(() => import("./pages/TimeManagement"));
const Proposals = lazy(() => import("./pages/Proposals"));
const ScheduledTasks = lazy(() => import("./pages/ScheduledTasks"));
const SubscriptionPlans = lazy(() => import("./pages/SubscriptionPlans"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancelled = lazy(() => import("./pages/CheckoutCancelled"));
const ManageSubscription = lazy(() => import("./pages/ManageSubscription"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const EmailConfirmationSuccess = lazy(() => import("./pages/EmailConfirmationSuccess"));
const TaskManagement = lazy(() => import("./pages/TaskManagement"));
const InvoiceDemo = lazy(() => import("./pages/InvoiceDemo"));
const DashboardDemo = lazy(() => import("./pages/DashboardDemo"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const ConditionalRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null; // Return nothing while loading
  
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />;
};

const App = () => {
  // Remove StrictMode in production for better performance
  const Wrapper = import.meta.env.PROD ? React.Fragment : React.StrictMode;
  
  return (
    <Wrapper>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <DemoDataProvider>
              <TooltipProvider>
                <SubscriptionLimitsProvider>
                  <DashboardCustomizationProvider>
                    <GlobalCommandPalette />
                    <ShortcutsHelpDialog />
                    <Toaster />
                    <Sonner />
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/subscription-plans" element={<SubscriptionPlans />} />
                      <Route path="/checkout-success" element={<CheckoutSuccess />} />
                      <Route path="/checkout-cancelled" element={<CheckoutCancelled />} />
                      <Route path="/email-confirmation-success" element={<EmailConfirmationSuccess />} />
                      
                      <Route path="/task-management-demo" element={<TaskManagement />} />
                      <Route path="/invoice-demo" element={<InvoiceDemo />} />
                      <Route path="/dashboard-demo" element={<DashboardDemo />} />
                      
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      
                      <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/clients" element={<Clients />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/project-tasks" element={<ProjectTasks />} />
                        <Route path="/invoices" element={<Invoices />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/time-management" element={<TimeManagement />} />
                        <Route path="/scheduled-tasks" element={<ScheduledTasks />} />
                        <Route path="/proposals" element={<Proposals />} />
                        <Route path="/manage-subscription" element={<ManageSubscription />} />
                        {/* Legacy route - redirect to dashboard */}
                        <Route path="/index" element={<Navigate to="/dashboard" replace />} />
                      </Route>
                      
                      <Route 
                        path="/admin/newsletter" 
                        element={
                          <ProtectedRoute requireAdmin>
                            <AdminNewsletter />
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route path="/landing" element={<Navigate to="/" replace />} />
                      
                      <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </DashboardCustomizationProvider>
                  </SubscriptionLimitsProvider>
                </TooltipProvider>
              </DemoDataProvider>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </Wrapper>
    );
};

export default App;
